/* Buscador de la cabecera (Dog Armour).
 *
 * Abre un panel bajo la cabecera, pide los resultados a la Section Rendering API
 * (/search/suggest?...&section_id=dar-predictive-search) y los pinta tal cual
 * vienen: el HTML lo compone Liquid, aqui no se formatea ni un precio.
 *
 * Decisiones que no son de estilo:
 *  - debounce 250 ms + AbortController: sin ellos, escribir "vest" dispara 4
 *    peticiones y la respuesta de "ves" puede llegar DESPUES que la de "vest".
 *  - cache por termino en un Map: borrar una letra no vuelve a pedir nada.
 *  - minimo 2 caracteres: con 1 el endpoint devuelve medio catalogo.
 *  - las flechas mueven el FOCO real entre los [role=option] (no un
 *    aria-activedescendant): asi funcionan igual el teclado y el lector.
 */
(() => {
  const MIN = 2;
  const ESPERA = 250;
  const RECIENTES_MAX = 5;
  const CLAVE = 'dar:recent-searches';

  class DarSearch {
    constructor(raiz) {
      this.raiz = raiz;
      this.panel = raiz.querySelector('[data-dar-search-panel]');
      this.input = raiz.querySelector('[data-dar-search-input]');
      this.resultados = raiz.querySelector('[data-dar-search-results]');
      this.defecto = raiz.querySelector('[data-dar-search-default]');
      this.limpiar = raiz.querySelector('[data-dar-search-clear]');
      this.recientes = raiz.querySelector('[data-dar-search-recent]');
      this.recientesLista = raiz.querySelector('[data-dar-search-recent-list]');
      this.formulario = raiz.querySelector('form');
      this.urlSugerencias = raiz.dataset.suggestUrl || '/search/suggest';
      this.seccion = raiz.dataset.sectionId || 'dar-predictive-search';
      this.disparadores = document.querySelectorAll('[data-dar-search-open]');

      this.cache = new Map();
      this.peticion = null;
      this.temporizador = 0;
      this.abierto = false;

      this.enTecla = this.enTecla.bind(this);
      this.enClicFuera = this.enClicFuera.bind(this);

      // el disparador es un <a href="/search"> para que funcione sin JS: aqui se
      // intercepta y se abre el panel en su lugar
      this.disparadores.forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.abrir();
        })
      );
      raiz.querySelectorAll('[data-dar-search-close]').forEach((b) => b.addEventListener('click', () => this.cerrar()));
      this.input.addEventListener('input', () => this.alEscribir());
      this.input.addEventListener('keydown', (e) => this.enTeclaInput(e));
      this.limpiar?.addEventListener('click', () => this.vaciar());
      this.formulario?.addEventListener('submit', () => this.guardarReciente(this.input.value.trim()));
      this.resultados.addEventListener('click', (e) => {
        const enlace = e.target.closest('a');
        if (enlace) this.guardarReciente(this.input.value.trim());
      });

      raiz.querySelector('[data-dar-search-wipe]')?.addEventListener('click', () => this.borrarRecientes());

      this.pintarRecientes();
    }

    /* ---------- abrir / cerrar ---------- */

    abrir() {
      if (this.abierto) return;
      this.abierto = true;
      this.raiz.hidden = false;
      // el navegador necesita un frame con el panel ya en el flujo para animar
      requestAnimationFrame(() => this.raiz.classList.add('is-open'));
      this.disparadores.forEach((b) => b.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('dar-search-abierto');
      document.addEventListener('keydown', this.enTecla);
      document.addEventListener('click', this.enClicFuera, true);
      this.input.focus();
      this.input.select();
    }

    cerrar() {
      if (!this.abierto) return;
      this.abierto = false;
      this.raiz.classList.remove('is-open');
      this.disparadores.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('dar-search-abierto');
      document.removeEventListener('keydown', this.enTecla);
      document.removeEventListener('click', this.enClicFuera, true);
      // devolver el foco a quien lo abrio: perderlo al cerrar deja al teclado al principio de la pagina
      this.disparadores[0]?.focus();
      const fin = () => { if (!this.abierto) this.raiz.hidden = true; };
      this.panel.addEventListener('transitionend', fin, { once: true });
      setTimeout(fin, 300);
    }

    enClicFuera(e) {
      if (!this.abierto) return;
      if (this.panel.contains(e.target)) return;
      if (e.target.closest('[data-dar-search-open]')) return;
      this.cerrar();
    }

    enTecla(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.cerrar();
        return;
      }
      if (e.key === 'Tab' && this.abierto) this.atraparFoco(e);
    }

    atraparFoco(e) {
      const focos = this.panel.querySelectorAll('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])');
      if (!focos.length) return;
      const primero = focos[0];
      const ultimo = focos[focos.length - 1];
      if (e.shiftKey && document.activeElement === primero) {
        e.preventDefault();
        ultimo.focus();
      } else if (!e.shiftKey && document.activeElement === ultimo) {
        e.preventDefault();
        primero.focus();
      }
    }

    /* ---------- teclado dentro del campo ---------- */

    enTeclaInput(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.mover(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.mover(-1);
      }
    }

    opciones() {
      const zona = this.resultados.querySelector('.dar-ps') ? this.resultados : this.defecto;
      return Array.from(zona?.querySelectorAll('[role="option"]') || []);
    }

    mover(paso) {
      const ops = this.opciones();
      if (!ops.length) return;
      const actual = ops.indexOf(document.activeElement);
      const siguiente = actual === -1 ? (paso > 0 ? 0 : ops.length - 1) : actual + paso;
      if (siguiente < 0) {
        this.input.focus();
        return;
      }
      const destino = ops[Math.min(siguiente, ops.length - 1)];
      ops.forEach((o) => o.setAttribute('aria-selected', 'false'));
      destino.setAttribute('aria-selected', 'true');
      destino.focus();
    }

    /* ---------- busqueda ---------- */

    alEscribir() {
      const q = this.input.value.trim();
      this.limpiar?.toggleAttribute('hidden', q.length === 0);
      clearTimeout(this.temporizador);

      if (q.length < MIN) {
        this.mostrarDefecto();
        return;
      }
      this.temporizador = setTimeout(() => this.buscar(q), ESPERA);
    }

    vaciar() {
      this.input.value = '';
      this.limpiar?.setAttribute('hidden', '');
      this.mostrarDefecto();
      this.input.focus();
    }

    mostrarDefecto() {
      this.resultados.innerHTML = '';
      this.raiz.dataset.state = 'idle';
      this.input.setAttribute('aria-expanded', 'false');
    }

    async buscar(q) {
      if (this.cache.has(q)) {
        this.pintar(this.cache.get(q));
        return;
      }
      this.raiz.dataset.state = 'loading';

      this.peticion?.abort();
      this.peticion = new AbortController();

      const url = new URL(this.urlSugerencias, window.location.origin);
      url.searchParams.set('q', q);
      url.searchParams.set('section_id', this.seccion);
      url.searchParams.set('resources[type]', 'product,collection,page,article,query');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const res = await fetch(url, { signal: this.peticion.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        this.cache.set(q, html);
        // si el usuario ha seguido escribiendo, esta respuesta ya no vale
        if (this.input.value.trim() !== q) return;
        this.pintar(html);
      } catch (err) {
        if (err.name === 'AbortError') return;
        // sin resultados en el panel, el formulario sigue llevando a /search
        this.mostrarDefecto();
      }
    }

    pintar(html) {
      this.resultados.innerHTML = html;
      const bloque = this.resultados.querySelector('.dar-ps');
      const hay = bloque && Number(bloque.dataset.total || 0) > 0;
      this.raiz.dataset.state = hay ? 'results' : 'empty';
      this.input.setAttribute('aria-expanded', hay ? 'true' : 'false');
    }

    /* ---------- busquedas recientes ---------- */

    leerRecientes() {
      try {
        const v = JSON.parse(localStorage.getItem(CLAVE) || '[]');
        return Array.isArray(v) ? v.filter((t) => typeof t === 'string') : [];
      } catch (e) {
        return [];
      }
    }

    guardarReciente(termino) {
      if (!termino || termino.length < MIN) return;
      try {
        const lista = [termino, ...this.leerRecientes().filter((t) => t.toLowerCase() !== termino.toLowerCase())];
        localStorage.setItem(CLAVE, JSON.stringify(lista.slice(0, RECIENTES_MAX)));
      } catch (e) {
        /* modo privado: sin recientes, el buscador sigue funcionando */
      }
    }

    borrarRecientes() {
      try { localStorage.removeItem(CLAVE); } catch (e) { /* modo privado */ }
      if (this.recientesLista) this.recientesLista.innerHTML = '';
      if (this.recientes) this.recientes.hidden = true;
      this.input.focus();
    }

    pintarRecientes() {
      if (!this.recientes || !this.recientesLista) return;
      const lista = this.leerRecientes();
      if (!lista.length) return;
      const base = this.raiz.dataset.searchUrl || '/search';
      this.recientesLista.innerHTML = lista
        .map((t) => {
          const seguro = t.replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]);
          return `<li><a class="dar-search__chip" role="option" tabindex="-1" aria-selected="false" href="${base}?q=${encodeURIComponent(t)}">${seguro}</a></li>`;
        })
        .join('');
      this.recientes.hidden = false;
    }
  }

  const arrancar = () => {
    const raiz = document.querySelector('[data-dar-search]');
    if (raiz && !raiz.darSearch) raiz.darSearch = new DarSearch(raiz);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arrancar);
  } else {
    arrancar();
  }
  // el editor de temas rehace la seccion de la cabecera al cambiar un ajuste
  document.addEventListener('shopify:section:load', arrancar);
})();
